import {
  STATUSES,
  describeAnchor,
  formatStatus,
  isClosed,
  statusTone,
  type Anchor,
  type Status,
  type Thread,
} from '@annotate/shared';
import { Api } from './api.js';
import {
  buildTextIndex,
  capturePin,
  captureRegion,
  captureText,
  resolveAnchor,
  type PageRect,
  type Placement,
  type TextIndex,
} from './anchor.js';
import { endReviewSession, storeName, storedName, type WidgetConfig } from './config.js';
import { FILTER_ICON, clear, el, icon, shortTime } from './dom.js';
import { HOST_ATTR } from './selector.js';
import { CSS } from './styles.js';

type Mode = 'off' | 'idle' | 'pin' | 'text' | 'region';

interface Placed {
  thread: Thread;
  placement: Placement;
}

interface Orphan {
  thread: Thread;
  reason: string;
}

export class Overlay {
  private readonly host = el('div', { [HOST_ATTR]: 'true' });
  private readonly root: ShadowRoot;
  private readonly layer = el('div', { class: 'layer' });
  private readonly chrome = el('div');

  private threads: Thread[] = [];
  private placed: Placed[] = [];
  private orphans: Orphan[] = [];
  private index: TextIndex = { text: '', chunks: [] };

  private mode: Mode = 'off';

  /**
   * What the sidebar — and the page — are showing.
   *
   * Applied to the drawn markers as well as the list, which is the point at a
   * hundred comments: narrowing to "needs review" should quiet the page down,
   * not just the panel beside it.
   *
   * `outstanding` is the default and means what "hide closed" used to. It is a
   * status filter rather than a separate toggle because two controls that both
   * decide whether resolved comments are visible will eventually disagree.
   */
  private filters: {
    status: Status | 'all' | 'outstanding' | 'closed';
    author: string | 'all';
    assignee: string | 'all' | 'none';
    scope: 'all' | 'page' | 'orphaned';
  } = { status: 'outstanding', author: 'all', assignee: 'all', scope: 'all' };
  private filtersOpen = false;
  /**
   * Where the sidebar list was scrolled to.
   *
   * Every reload rebuilds the chrome from scratch, so the list is a new element
   * and starts at the top. Working down a hundred comments — open one, set a
   * status, come back — that threw you to the top after every single change.
   */
  private listScroll = 0;
  private sidebarOpen = false;
  private activeThreadId: string | null = null;

  private popover: HTMLElement | null = null;
  private popoverAt: { x: number; y: number } | null = null;
  /**
   * What the open popover has typed in it, if anything.
   *
   * Read before dismissing on an outside click. Clicking away is a gesture you
   * make by accident — reaching for the page to check something, missing the
   * panel edge — and the thing it would throw away is the sentence the reviewer
   * came here to write. That is the one piece of work in this tool nobody can
   * reconstruct.
   */
  private popoverDraft: (() => string) | null = null;
  /**
   * Set when a composer is opened from `mouseup` — text selection and region
   * drags both do.
   *
   * A click always follows the mouseup of the same gesture, so without this the
   * outside-click dismissal reads that trailing click as "the user clicked away"
   * and closes the composer the instant it appears. Pins are unaffected: they
   * open from inside the click handler itself, which then returns.
   */
  private swallowNextClick = false;
  private dragFrom: { x: number; y: number } | null = null;
  private dragBox: HTMLElement | null = null;

  private frame = 0;

  constructor(
    private readonly api: Api,
    private readonly config: WidgetConfig,
  ) {
    this.root = this.host.attachShadow({ mode: 'open' });
    this.root.appendChild(el('style', { text: CSS }));
    this.root.appendChild(this.layer);
    this.root.appendChild(this.chrome);
    document.body.appendChild(this.host);

    this.listen();
  }

  // ── lifecycle ─────────────────────────────────────────────────────────

  async start(): Promise<void> {
    const focusId = this.config.focusThreadId;

    // Review mode goes on *before* the first load, not after.
    //
    // `repaint` does nothing while the mode is 'off', so loading first left
    // `placed` empty and every deep link then reported its comment as unplaceable
    // and dropped the reader at the top of the page — with a toast blaming the
    // page for having changed, which it had not.
    if (focusId) this.mode = 'idle';

    this.renderChrome();
    await this.reload();

    if (!focusId) return;

    const target = this.threads.find((t) => t.id === focusId);
    if (!target) {
      this.toast('That comment is not on this page.');
      return;
    }

    this.focus(target);

    // Images and webfonts land after DOMContentLoaded and move everything below
    // them, so a scroll computed now can be hundreds of pixels out by the time the
    // reader is looking. Re-resolve and re-aim once the page has finished settling.
    window.addEventListener(
      'load',
      () => {
        this.repaint();
        this.scrollToThread(target.id);
        this.positionPopover();
      },
      { once: true },
    );
  }

  private async reload(): Promise<void> {
    const res = await this.api.list();
    if (!res.ok) {
      this.toast(res.error);
      return;
    }
    this.threads = res.value.threads;
    this.repaint();
  }

  /**
   * Re-resolve every anchor and redraw.
   *
   * Anchors are resolved against the page as it is now, every time — not cached
   * from when the comment was made. That is the whole point: if the content has
   * changed underneath a comment, this is where we find out, and the ones that
   * no longer resolve go to the orphan tray instead of being drawn somewhere
   * that merely looks right.
   */
  private repaint = (): void => {
    if (this.mode === 'off') {
      clear(this.layer);
      this.renderChrome();
      return;
    }

    this.index = buildTextIndex();
    this.placed = [];
    this.orphans = [];

    for (const thread of this.threads) {
      if (!this.matches(thread)) continue;
      const res = resolveAnchor(thread.anchor, this.index);
      if (res.ok) this.placed.push({ thread, placement: res.placement });
      else this.orphans.push({ thread, reason: res.reason });
    }

    this.drawMarkers();
    this.renderChrome();
    this.positionPopover();
  };

  private schedule = (): void => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.repaint();
    });
  };

  // ── drawing ───────────────────────────────────────────────────────────

  private drawMarkers(): void {
    clear(this.layer);

    for (const { thread, placement } of this.placed) {
      for (const rect of placement.rects) {
        this.layer.appendChild(
          el('div', {
            class: thread.anchor.kind === 'region' ? 'region-box' : 'hl',
            style: boxStyle(rect),
          }),
        );
      }

      const marker = el(
        'button',
        {
          class: `marker tone-${statusTone(thread.status)}${
            thread.id === this.activeThreadId ? ' active' : ''
          }`,
          style: `left:${placement.x}px; top:${placement.y}px`,
          title: `${formatStatus(thread.status)} · ${thread.authorName}: ${
            thread.comments[0]?.body ?? ''
          }`,
        },
        String(thread.seq),
      );
      marker.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openThread(thread, { x: placement.x, y: placement.y });
      });
      this.layer.appendChild(marker);
    }
  }

  // ── chrome: launcher, toolbar, sidebar ────────────────────────────────

  private renderChrome(): void {
    // Read before the teardown, fall back to the last known value when the
    // sidebar is being opened rather than re-rendered.
    const scroller = this.root.querySelector('.list');
    if (scroller) this.listScroll = scroller.scrollTop;

    clear(this.chrome);

    if (this.mode === 'off') {
      const outstanding = this.threads.filter((t) => !isClosed(t.status)).length;
      const pill = el(
        'button',
        { class: 'pill', title: 'Review this page' },
        el('span', { class: 'dot' }),
        outstanding > 0 ? `${outstanding} comment${outstanding === 1 ? '' : 's'}` : 'Comment',
      );
      pill.addEventListener('click', () => this.setMode('idle'));
      this.chrome.appendChild(pill);
      return;
    }

    const toolbar = el('div', { class: 'toolbar' });
    toolbar.appendChild(this.toolButton('pin', '📍', 'Pin'));
    toolbar.appendChild(this.toolButton('text', '✏️', 'Text'));
    toolbar.appendChild(this.toolButton('region', '⬚', 'Region'));
    toolbar.appendChild(el('div', { class: 'sep' }));

    // Outstanding, not total — the same number the launcher shows. A count that
    // includes resolved items overstates what is left to do, which is the one
    // thing a reviewer is reading it for.
    const outstanding = this.threads.filter((t) => !isClosed(t.status)).length;
    const listBtn = el(
      'button',
      { class: `tool icon${this.sidebarOpen ? ' on' : ''}`, title: 'Comments' },
      `☰ ${outstanding}`,
    );
    listBtn.addEventListener('click', () => {
      this.sidebarOpen = !this.sidebarOpen;
      this.renderChrome();
    });
    toolbar.appendChild(listBtn);

    // Ends the session, not just this page. Review mode now survives navigation,
    // so a close that only hid the overlay here would reappear on the next link
    // and read as a bug — and there would be no way out short of typing a query
    // parameter, which is the friction this was meant to remove.
    const closeBtn = el(
      'button',
      { class: 'tool icon', title: 'Leave review mode for this site (Esc)' },
      '✕',
    );
    closeBtn.addEventListener('click', () => {
      endReviewSession();
      this.setMode('off');
    });
    toolbar.appendChild(closeBtn);

    this.chrome.appendChild(toolbar);

    if (this.sidebarOpen) {
      const sidebar = this.renderSidebar();
      this.chrome.appendChild(sidebar);
      // After it is in the tree: scrollTop on a detached element does not stick.
      const list = sidebar.querySelector('.list');
      if (list) list.scrollTop = this.listScroll;
    }
  }

  private toolButton(mode: Mode, icon: string, label: string): HTMLElement {
    const active = this.mode === mode;
    const btn = el('button', { class: `tool${active ? ' on' : ''}` }, `${icon} ${label}`);
    btn.addEventListener('click', () => this.setMode(active ? 'idle' : mode));
    return btn;
  }

  /** One rule for the list and the markers, so the page and the panel can never
   *  disagree about what is being looked at. */
  private matches(thread: Thread): boolean {
    const f = this.filters;
    if (f.status === 'outstanding' && isClosed(thread.status)) return false;
    if (f.status === 'closed' && !isClosed(thread.status)) return false;
    if (
      f.status !== 'all' &&
      f.status !== 'outstanding' &&
      f.status !== 'closed' &&
      thread.status !== f.status
    ) {
      return false;
    }
    if (f.author !== 'all' && thread.authorName !== f.author) return false;
    if (f.assignee === 'none' && thread.assignee) return false;
    if (f.assignee !== 'all' && f.assignee !== 'none' && thread.assignee !== f.assignee) return false;
    return true;
  }

  private activeFilterCount(): number {
    const f = this.filters;
    return (
      (f.status === 'outstanding' ? 0 : 1) +
      (f.author === 'all' ? 0 : 1) +
      (f.assignee === 'all' ? 0 : 1) +
      (f.scope === 'all' ? 0 : 1)
    );
  }

  /**
   * The filter panel, behind the funnel rather than always on show.
   *
   * Counts come from every thread on the page, not the filtered set, so the
   * options keep saying where the rest of the work is instead of collapsing to
   * whatever is already on screen.
   */
  private renderFilters(): HTMLElement {
    const authors = new Map<string, number>();
    const assignees = new Map<string, number>();
    const statuses = new Map<Status, number>();
    let unassigned = 0;
    for (const t of this.threads) {
      authors.set(t.authorName, (authors.get(t.authorName) ?? 0) + 1);
      statuses.set(t.status, (statuses.get(t.status) ?? 0) + 1);
      if (t.assignee) assignees.set(t.assignee, (assignees.get(t.assignee) ?? 0) + 1);
      else unassigned++;
    }
    const outstanding = this.threads.filter((t) => !isClosed(t.status)).length;

    const field = (
      label: string,
      options: { value: string; label: string }[],
      current: string,
      onPick: (value: string) => void,
    ): HTMLElement => {
      const select = el('select', {});
      for (const o of options) {
        const option = el('option', { value: o.value, text: o.label });
        if (o.value === current) option.setAttribute('selected', 'true');
        select.appendChild(option);
      }
      select.addEventListener('change', (e) => {
        onPick((e.target as HTMLSelectElement).value);
        this.repaint();
      });
      return el('label', { class: 'filter-field' }, el('span', { text: label }), select);
    };

    const panel = el('div', { class: 'filters' });

    panel.appendChild(
      // Labelled "Show" because that is how people ask for it, and `closed` sits
      // here beside `outstanding` rather than in the scope selector below. Both
      // are groupings of status, and putting one of them under a second control
      // is how you end up with a screen that says "Outstanding" and "Closed only"
      // at the same time.
      field(
        'Show',
        [
          { value: 'outstanding', label: `Outstanding (${outstanding})` },
          { value: 'closed', label: `Closed (${this.threads.length - outstanding})` },
          { value: 'all', label: `Everything (${this.threads.length})` },
          ...STATUSES.map((st) => ({
            value: st,
            label: `${formatStatus(st)} (${statuses.get(st) ?? 0})`,
          })),
        ],
        this.filters.status,
        (v) => (this.filters.status = v as Status | 'all' | 'outstanding' | 'closed'),
      ),
    );

    panel.appendChild(
      field(
        'From',
        [
          { value: 'all', label: 'Anyone' },
          ...[...authors.entries()].map(([n, c]) => ({ value: n, label: `${n} (${c})` })),
        ],
        this.filters.author,
        (v) => (this.filters.author = v),
      ),
    );

    panel.appendChild(
      field(
        'Assigned to',
        [
          { value: 'all', label: 'Anyone' },
          { value: 'none', label: `Nobody (${unassigned})` },
          ...[...assignees.entries()].map(([n, c]) => ({ value: n, label: `${n} (${c})` })),
        ],
        this.filters.assignee,
        (v) => (this.filters.assignee = v as string | 'all' | 'none'),
      ),
    );

    // Orphans used to be a group pinned under the list, which is where you find
    // them only if you scroll past everything else. As a scope they are one
    // choice away, and can be looked at on their own.
    panel.appendChild(
      field(
        'Where',
        [
          { value: 'all', label: 'On page and orphaned' },
          { value: 'page', label: 'On this page only' },
          { value: 'orphaned', label: `Orphaned only (${this.orphans.length})` },
        ],
        this.filters.scope,
        (v) => (this.filters.scope = v as 'all' | 'page' | 'orphaned'),
      ),
    );

    if (this.activeFilterCount() > 0) {
      const clearBtn = el('button', { class: 'ghost' }, 'Clear filters');
      clearBtn.addEventListener('click', () => {
        this.filters = { status: 'outstanding', author: 'all', assignee: 'all', scope: 'all' };
        this.repaint();
      });
      panel.appendChild(clearBtn);
    }

    return panel;
  }

  private renderSidebar(): HTMLElement {
    const list = el('div', { class: 'list' });

    if (this.filters.scope !== 'orphaned') {
      for (const { thread } of this.placed) {
        // `focus` rather than `openThread`: scroll the page to the marker first,
        // then open it there. Opening at wherever the page happened to be
        // scrolled put the panel next to something unrelated, which is most of
        // what made a long list unusable.
        list.appendChild(this.sidebarItem(thread, () => this.focus(thread)));
      }
    }

    if (this.filters.scope !== 'page' && this.orphans.length > 0) {
      list.appendChild(
        el('div', { class: 'group' }, `Orphaned (${this.orphans.length}) — content moved`),
      );
      for (const { thread, reason } of this.orphans) {
        const item = this.sidebarItem(thread, () => this.focus(thread));
        item.appendChild(el('span', { class: 'orphan-why', text: reason }));
        list.appendChild(item);
      }
    }

    const shown =
      (this.filters.scope === 'orphaned' ? 0 : this.placed.length) +
      (this.filters.scope === 'page' ? 0 : this.orphans.length);

    if (shown === 0) {
      list.appendChild(
        el(
          'div',
          { class: 'blank' },
          this.threads.length === 0
            ? 'No comments yet. Pick a tool and point at something.'
            : 'Nothing matches these filters.',
        ),
      );
    }

    const active = this.activeFilterCount();
    const funnel = el(
      'button',
      {
        class: `ghost filter-btn${this.filtersOpen || active > 0 ? ' on' : ''}`,
        title: active > 0 ? `Filters (${active} active)` : 'Filter',
      },
      icon(FILTER_ICON),
      active > 0 ? el('span', { text: String(active) }) : null,
    );
    funnel.addEventListener('click', () => {
      this.filtersOpen = !this.filtersOpen;
      this.renderChrome();
    });

    const sidebar = el(
      'div',
      { class: 'sidebar' },
      el('h3', {}, `Comments (${shown})`, funnel),
    );
    if (this.filtersOpen) sidebar.appendChild(this.renderFilters());
    sidebar.appendChild(list);
    return sidebar;
  }

  private sidebarItem(thread: Thread, onClick: () => void): HTMLElement {
    const item = el(
      'button',
      { class: 'item' },
      el(
        'span',
        { class: 'who' },
        `#${thread.seq} ${thread.authorName}`,
        el('span', { class: 'when', text: shortTime(thread.createdAt) }),
      ),
      el('span', { class: 'snip', text: thread.comments[0]?.body ?? '' }),
    );
    item.addEventListener('click', onClick);
    return item;
  }

  // ── modes ─────────────────────────────────────────────────────────────

  private setMode(mode: Mode): void {
    this.mode = mode;
    this.closePopover();

    const placing = mode === 'pin' || mode === 'region';
    document.documentElement.style.cursor = placing ? 'crosshair' : '';

    if (mode === 'off') {
      this.sidebarOpen = false;
      this.activeThreadId = null;
    }
    this.repaint();

    if (mode === 'text') this.toast('Select some text to comment on it.');
    if (mode === 'region') this.toast('Drag a box around the area.');
  }

  private listen(): void {
    window.addEventListener('resize', this.schedule);
    window.addEventListener('scroll', () => this.positionPopover(), { passive: true });

    // Content that arrives late — images, fonts, a client-side render — moves
    // everything under the markers. Re-resolving is cheap; being wrong is not.
    const mo = new MutationObserver(() => {
      if (this.mode !== 'off') this.schedule();
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    if (document.fonts) void document.fonts.ready.then(this.schedule);

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || this.mode === 'off') return;
      if (this.popover) this.closePopover();
      else if (this.mode === 'idle') {
        // Same reasoning as the ✕ button: leaving review mode has to mean the
        // site, or it comes back on the next link.
        endReviewSession();
        this.setMode('off');
      } else this.setMode('idle');
    });

    // Capture phase, so a placement click never reaches the page's own handlers.
    // Without this, pinning a comment on a nav link navigates away from the page
    // you were reviewing.
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('mousedown', this.onMouseDown, true);
    document.addEventListener('mousemove', this.onMouseMove, true);
    document.addEventListener('mouseup', this.onMouseUp, true);
  }

  private onClick = (e: MouseEvent): void => {
    if (this.mode === 'off' || isInHost(e)) return;

    // The tail of the gesture that just opened a composer, not a new intent.
    if (this.swallowNextClick) {
      this.swallowNextClick = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // A click on the page with a popover open dismisses it, and does nothing
    // else. Swallowing the click matters either way: without it, dismissing a
    // composer in pin mode would place a second pin at the point you clicked to
    // get rid of the first, and clicking near a link would follow it.
    if (this.popover) {
      e.preventDefault();
      e.stopPropagation();

      // Anything typed and the gesture is refused. Clicking away is something
      // you do by accident; discarding a written comment because of it is not a
      // trade worth making, and there is no undo. Say so rather than appearing
      // to ignore the click, which is how a panel comes to look broken.
      if (this.popoverDraft?.().trim()) {
        this.toast('Esc or ✕ to discard what you have typed.');
        return;
      }

      this.closePopover();
      // Matches what Cancel does. Leaving the crosshair up after a dismissal
      // makes the next stray click start another comment.
      if (this.mode !== 'idle') this.setMode('idle');
      else this.repaint();
      return;
    }

    if (this.mode === 'pin') {
      e.preventDefault();
      e.stopPropagation();
      const anchor = capturePin(e.clientX, e.clientY);
      if (!anchor) {
        this.toast('Could not attach a comment there. Try slightly to the side.');
        return;
      }
      this.openComposer(anchor, { x: e.clientX + window.scrollX, y: e.clientY + window.scrollY });
      return;
    }

    // In every other mode the page stays usable, but links still must not fire —
    // a stray click on a nav item would abandon the review.
    const link = (e.target as Element | null)?.closest?.('a[href]');
    if (link) e.preventDefault();
  };

  private onMouseDown = (e: MouseEvent): void => {
    // Bounds the flag to one interaction. If a click never arrives after the
    // mouseup that set it, the next gesture clears it rather than letting it
    // swallow an unrelated click later.
    this.swallowNextClick = false;

    if (this.mode !== 'region' || isInHost(e)) return;
    e.preventDefault();
    e.stopPropagation();
    this.dragFrom = { x: e.clientX, y: e.clientY };
    document.documentElement.style.userSelect = 'none';
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.dragFrom) return;
    const rect = rectBetween(this.dragFrom, { x: e.clientX, y: e.clientY });
    if (!this.dragBox) {
      this.dragBox = el('div', { class: 'drag-box' });
      this.root.appendChild(this.dragBox);
    }
    this.dragBox.setAttribute(
      'style',
      `left:${rect.x}px; top:${rect.y}px; width:${rect.w}px; height:${rect.h}px`,
    );
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (this.mode === 'text' && !isInHost(e)) {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
        this.index = buildTextIndex();
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const anchor = captureText(range, this.index);
        selection.removeAllRanges();
        if (!anchor) {
          this.toast('Could not anchor to that selection. Try selecting within one block.');
          return;
        }
        this.swallowNextClick = true;
        this.openComposer(anchor, { x: rect.left + window.scrollX, y: rect.top + window.scrollY });
      }
      return;
    }

    if (!this.dragFrom) return;
    const from = this.dragFrom;
    this.dragFrom = null;
    document.documentElement.style.userSelect = '';
    this.dragBox?.remove();
    this.dragBox = null;

    const rect = rectBetween(from, { x: e.clientX, y: e.clientY });
    const anchor = captureRegion(rect);
    if (!anchor) {
      this.toast('That region is too small to anchor. Drag a larger box.');
      return;
    }
    this.swallowNextClick = true;
    this.openComposer(anchor, { x: rect.x + window.scrollX, y: rect.y + window.scrollY });
  };

  // ── popovers ──────────────────────────────────────────────────────────

  /**
   * The ✕ every popover carries.
   *
   * Esc has always closed these and clicking away now does too, but neither is
   * visible. Someone handed a review link has no reason to guess at either, and
   * a panel with no way out reads as the tool having hung — which is what
   * happened.
   */
  private dismissButton(): HTMLElement {
    const button = el('button', { class: 'dismiss', title: 'Close (Esc)' }, '✕');
    button.addEventListener('click', () => {
      this.closePopover();
      if (this.mode !== 'idle') this.setMode('idle');
      else this.repaint();
    });
    return button;
  }

  private openComposer(anchor: Anchor, at: { x: number; y: number }): void {
    this.closePopover();

    const name = el('input', { placeholder: 'Your name', value: storedName() });
    const body = el('textarea', { placeholder: 'What needs to change?' });
    const submit = el('button', { class: 'primary' }, 'Comment');
    const cancel = el('button', { class: 'ghost' }, 'Cancel');

    const send = async (): Promise<void> => {
      const who = name.value.trim();
      const what = body.value.trim();
      if (!who) return void name.focus();
      if (!what) return void body.focus();

      submit.setAttribute('disabled', 'true');
      submit.textContent = 'Saving…';
      storeName(who);

      const res = await this.api.create(anchor, what, who);
      if (!res.ok) {
        submit.removeAttribute('disabled');
        submit.textContent = 'Comment';
        this.toast(res.error);
        return;
      }
      this.closePopover();
      if (res.warning) this.toast(res.warning);
      this.setMode('idle');
      await this.reload();
    };

    submit.addEventListener('click', () => void send());
    cancel.addEventListener('click', () => {
      this.closePopover();
      this.setMode('idle');
    });
    body.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void send();
    });

    const panel = el(
      'div',
      { class: 'popover' },
      el(
        'div',
        { class: 'head' },
        el('span', { class: 'num' }, 'New comment'),
        this.dismissButton(),
      ),
      el('div', { class: 'quote', text: describeAnchor(anchor) }),
      storedName() ? null : name,
      body,
      el('div', { class: 'actions' }, submit, el('span', { class: 'spacer' }), cancel),
    );

    this.mount(panel, at);
    // After mount: `closePopover` runs at the top of every opener and clears it.
    this.popoverDraft = () => body.value;
    (storedName() ? body : name).focus();
  }

  private openThread(thread: Thread, at: { x: number; y: number } | null): void {
    this.closePopover();
    this.activeThreadId = thread.id;
    // Redraw so the open thread's marker actually picks up `.active`. Setting the
    // id without repainting left the highlight permanently invisible.
    this.drawMarkers();

    const panel = el('div', { class: 'popover' });
    panel.appendChild(
      el(
        'div',
        { class: 'head' },
        el('span', { class: 'num' }, `#${thread.seq}`),
        el(
          'span',
          { class: 'head-right' },
          el('span', {
            class: `badge tone-${statusTone(thread.status)}`,
            text: formatStatus(thread.status),
          }),
          this.dismissButton(),
        ),
      ),
    );
    panel.appendChild(el('div', { class: 'quote', text: describeAnchor(thread.anchor) }));

    // Comments and status changes share one ordered timeline, so a reply always
    // reads after the change that prompted it.
    for (const c of thread.comments) {
      if (c.kind === 'status') {
        panel.appendChild(
          el('div', { class: 'event', text: `${c.body} · ${shortTime(c.createdAt)}` }),
        );
        continue;
      }
      panel.appendChild(
        el(
          'div',
          { class: 'msg' },
          el(
            'div',
            { class: 'who' },
            c.authorName,
            el('span', { class: 'when', text: shortTime(c.createdAt) }),
          ),
          el('div', { class: 'text', text: c.body }),
        ),
      );
    }

    if (thread.clickupError) {
      panel.appendChild(
        el('div', { class: 'note', text: `Not in ClickUp yet: ${thread.clickupError}` }),
      );
    }

    const reply = el('textarea', { placeholder: 'Reply…' });
    const send = el('button', { class: 'primary' }, 'Reply');

    const picker = el('select', { class: 'status-picker', title: 'Status' });
    for (const s of STATUSES) {
      const option = el('option', { value: s, text: formatStatus(s) });
      if (s === thread.status) option.setAttribute('selected', 'true');
      picker.appendChild(option);
    }

    const who = (): string => storedName() || 'Anonymous';

    picker.addEventListener('change', async () => {
      const next = picker.value as Status;
      picker.setAttribute('disabled', 'true');
      const res = await this.api.setStatus(thread.id, next, who());
      picker.removeAttribute('disabled');
      if (!res.ok) {
        // Snap back rather than leave the picker asserting a change that did not
        // land on the server.
        picker.value = thread.status;
        this.toast(res.error);
        return;
      }
      if (res.warning) this.toast(res.warning);
      this.closePopover();
      await this.reload();
    });

    send.addEventListener('click', async () => {
      const what = reply.value.trim();
      if (!what) return void reply.focus();
      send.setAttribute('disabled', 'true');
      send.textContent = 'Sending…';
      const res = await this.api.reply(thread.id, what, who());
      if (!res.ok) {
        send.removeAttribute('disabled');
        send.textContent = 'Reply';
        this.toast(res.error);
        return;
      }
      if (res.warning) this.toast(res.warning);
      this.closePopover();
      await this.reload();
    });

    panel.appendChild(reply);
    panel.appendChild(
      el('div', { class: 'actions' }, send, el('span', { class: 'spacer' }), picker),
    );

    this.mount(panel, at);
    // A half-written reply is worth the same protection as a half-written
    // comment — it is the same person, mid-sentence.
    this.popoverDraft = () => reply.value;
  }

  /** Scroll a placed thread's marker into comfortable view. Returns false if the
   *  thread has no position, which is not the same as "it went wrong". */
  private scrollToThread(threadId: string): boolean {
    const found = this.placed.find((p) => p.thread.id === threadId);
    if (!found) return false;
    window.scrollTo({
      top: Math.max(0, found.placement.y - window.innerHeight / 3),
      behavior: 'smooth',
    });
    return true;
  }

  /**
   * Bring a thread into view and open it — used by links from the project view
   * and from ClickUp tickets.
   */
  private focus(thread: Thread): void {
    // Following a link to a resolved or won't-fix comment is normal — checking
    // what was decided is half the reason the link exists. Closed comments are
    // hidden by default, so reveal them rather than sending someone to a page
    // where the marker they were promised does not exist.
    // Following a link to a resolved comment is normal, so widen the filter
    // rather than sending someone to a page where the promised marker is
    // filtered out.
    if (!this.matches(thread)) {
      this.filters = { status: 'all', author: 'all', assignee: 'all', scope: 'all' };
      this.repaint();
    }

    const found = this.placed.find((p) => p.thread.id === thread.id);
    if (found) {
      this.scrollToThread(thread.id);
      this.openThread(thread, found.placement);
      return;
    }

    // Genuinely unplaceable. Say which of the two reasons it is instead of
    // asserting the page changed, which was often simply untrue.
    const orphan = this.orphans.find((o) => o.thread.id === thread.id);
    this.sidebarOpen = true;
    this.renderChrome();
    this.openThread(thread, null);
    this.toast(
      orphan
        ? `Showing this comment out of context — ${orphan.reason}.`
        : 'This comment could not be placed on the page.',
    );
  }

  private mount(panel: HTMLElement, at: { x: number; y: number } | null): void {
    this.popover = panel;
    this.popoverAt = at;
    this.root.appendChild(panel);
    this.positionPopover();
  }

  /** Keep the popover beside its marker but always fully on screen. */
  private positionPopover(): void {
    if (!this.popover) return;

    if (!this.popoverAt) {
      this.popover.style.left = '50%';
      this.popover.style.top = '80px';
      this.popover.style.transform = 'translateX(-50%)';
      return;
    }

    const width = this.popover.offsetWidth || 328;
    const height = this.popover.offsetHeight || 200;
    const vx = this.popoverAt.x - window.scrollX;
    const vy = this.popoverAt.y - window.scrollY;

    const left = Math.min(Math.max(12, vx + 18), window.innerWidth - width - 12);
    const top = Math.min(Math.max(12, vy - 10), window.innerHeight - height - 12);

    this.popover.style.transform = '';
    this.popover.style.left = `${left}px`;
    this.popover.style.top = `${top}px`;
  }

  private closePopover(): void {
    this.popover?.remove();
    this.popover = null;
    this.popoverAt = null;
    this.popoverDraft = null;
    this.activeThreadId = null;
  }

  private toast(message: string): void {
    this.root.querySelector('.toast')?.remove();
    const node = el('div', { class: 'toast', text: message });
    this.root.appendChild(node);
    setTimeout(() => node.remove(), 5200);
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

function isInHost(e: Event): boolean {
  return e.composedPath().some((n) => n instanceof Element && n.hasAttribute(HOST_ATTR));
}

function rectBetween(a: { x: number; y: number }, b: { x: number; y: number }): PageRect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

function boxStyle(rect: PageRect): string {
  return `left:${rect.x}px; top:${rect.y}px; width:${rect.w}px; height:${rect.h}px`;
}
